import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StudentDocumentService } from '../../../../../../services/student-document.service';
import { GlobalConfig, ToastrService } from 'ngx-toastr';
import { AlertToast } from '../../../../../../shared/notifications/alert.toast';
import { RejectReasonModalComponent } from '../../../../../../shared/components/reject-reason-modal/reject-reason-modal.component';
import { TooltipModule } from 'primeng/tooltip';

interface StudentDocument {
  id: number;
  user_id: number;
  document_type: string;
  document_type_label: string;
  expiration_date: string | null;
  notes: string | null;
  is_expired: boolean;
  days_until_expiration: number | null;
  verified_at: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  status_label: string;
  review_feedback: string | null;
  created_at: string;
  uploaded_by: {
    id: number;
    name: string;
  };
  media: any[];
}

@Component({
  selector: 'app-general-documents-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RejectReasonModalComponent, TooltipModule],
  templateUrl: './general-documents-list.component.html',
  styleUrl: './general-documents-list.component.css'
})
export class GeneralDocumentsListComponent implements OnInit, OnChanges {
  @Input() userId!: number;
  @Output() openModal = new EventEmitter<{isEditing: boolean, document?: any, formData?: any}>();
  @Output() closeModalEvent = new EventEmitter<void>();
  @Output() openConfirmation = new EventEmitter<{question: string, action: () => void}>();

  documents: StudentDocument[] = [];
  loading: boolean = false;
  showModal: boolean = false;
  isEditing: boolean = false;
  currentDocument: any = null;
  deleting: boolean = false;
  
  // Modal de confirmación
  showConfirmation: boolean = false;
  confirmationQuestion: string = '';
  confirmationAction: (() => void) | null = null;

  // Formulario
  formData = {
    document_type: '',
    expiration_date: '',
    notes: '',
    file: null as File | null
  };

  documentTypes = [
    { id: 'FOTO', name: 'Fotografia' },
    { id: 'JUSTIFICANTE_PAGO', name: 'Justificante de Pago' },
    { id: 'EXPEDIENTE', name: 'Expediente Completo' },
    { id: 'OTROS', name: 'Otros' }
  ];
  options: GlobalConfig;

  constructor(
    private documentService: StudentDocumentService,
    private toastr: ToastrService
  ) {
    this.options = this.toastr.toastrConfig;
  }

  ngOnInit(): void {
    this.loadDocuments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId'] && this.userId) {
      this.loadDocuments();
    }
  }

  loadDocuments(): void {
    if (!this.userId) return;

    this.loading = true;
    this.documentService.getDocuments(this.userId, 50, undefined, { general_only: true }).subscribe({
      next: (response) => {
        this.documents = response.data?.data || [];
        this.loading = false;
      },
      error: (error) => {
        console.error('Error al cargar documentos:', error);
        this.loading = false;
      }
    });
  }

  openCreateModal(): void {
    this.isEditing = false;
    this.currentDocument = null;
    this.formData = {
      document_type: '',
      expiration_date: '',
      notes: '',
      file: null
    };
    this.openModal.emit({isEditing: false, formData: this.formData});
  }

  openEditModal(document: StudentDocument): void {
    this.isEditing = true;
    this.currentDocument = document;
    
    // Formatear la fecha para el input type="date" (YYYY-MM-DD)
    // Usar split directamente para evitar problemas de zona horaria
    let formattedDate = '';
    if (document.expiration_date) {
      formattedDate = document.expiration_date.split('T')[0];
    }
    
    this.formData = {
      document_type: document.document_type,
      expiration_date: formattedDate,
      notes: document.notes || '',
      file: null
    };
    this.openModal.emit({isEditing: true, document, formData: this.formData});
  }

  closeModal(): void {
    this.showModal = false;
    this.currentDocument = null;
    this.closeModalEvent.emit();
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.formData.file = file;
    }
  }

  saveDocument(): void {
    if (this.isEditing) {
      this.updateDocument();
    } else {
      this.createDocument();
    }
  }

  createDocument(): void {
    if (!this.formData.document_type || !this.formData.file) {
      this.errorToast('Error', 'Debe seleccionar un tipo de documento y subir un archivo');
      return;
    }

    const data = new FormData();
    data.append('user_id', this.userId.toString());
    data.append('document_type', this.formData.document_type);
    if (this.formData.expiration_date) {
      // Enviar la fecha tal cual está en formato YYYY-MM-DD sin conversión de zona horaria
      data.append('expiration_date', this.formData.expiration_date);
    }
    if (this.formData.notes) {
      data.append('notes', this.formData.notes);
    }
    data.append('file', this.formData.file);

    this.loading = true;
    this.documentService.uploadDocument(data).subscribe({
      next: (response) => {
        this.successToast('Éxito', 'Documento subido correctamente');
        this.closeModal();
        this.loadDocuments();
      },
      error: (error) => {
        console.error('Error al subir documento:', error);
        this.errorToast('Error', error.error?.message || 'Error al subir el documento');
        this.loading = false;
      }
    });
  }

  updateDocument(): void {
    if (!this.currentDocument) return;

    const data: any = {
      document_type: this.formData.document_type,
      notes: this.formData.notes || null
    };

    // Enviar la fecha tal cual está en formato YYYY-MM-DD sin conversión de zona horaria
    if (this.formData.expiration_date) {
      data.expiration_date = this.formData.expiration_date;
    } else {
      data.expiration_date = null;
    }

    this.loading = true;
    this.documentService.updateDocument(this.currentDocument.id, data).subscribe({
      next: (response) => {
        this.successToast('Éxito', 'Documento actualizado correctamente');
        this.closeModal();
        this.loadDocuments();
      },
      error: (error) => {
        console.error('Error al actualizar documento:', error);
        this.errorToast('Error', error.error?.message || 'Error al actualizar el documento');
        this.loading = false;
      }
    });
  }

  deleteDocument(document: StudentDocument): void {
    const action = () => {
      this.deleting = true;
      this.documentService.deleteDocument(document.id).subscribe({
        next: () => {
          this.successToast('Éxito', 'Documento eliminado correctamente');
          this.loadDocuments();
          this.deleting = false;
        },
        error: (error) => {
          console.error('Error al eliminar documento:', error);
          this.errorToast('Error', error.error?.message || 'Error al eliminar el documento');
          this.deleting = false;
        }
      });
    };
    this.openConfirmation.emit({question: '¿Está seguro de eliminar este documento?', action});
  }

  verifyDocument(document: StudentDocument): void {
    const action = () => {
      this.documentService.verifyDocument(document.id).subscribe({
        next: () => {
          this.successToast('Éxito', 'Documento verificado correctamente');
          this.loadDocuments();
        },
        error: (error) => {
          console.error('Error al verificar documento:', error);
          this.errorToast('Error', error.error?.message || 'Error al verificar el documento');
        }
      });
    };
    this.openConfirmation.emit({question: '¿Aceptar este documento?', action});
  }

  rejectingDoc: StudentDocument | null = null;

  rejectDocument(document: StudentDocument): void {
    this.rejectingDoc = document;
  }

  confirmReject(feedback: string): void {
    const document = this.rejectingDoc;
    this.rejectingDoc = null;
    if (!document) {
      return;
    }
    this.documentService.rejectDocument(document.id, feedback).subscribe({
      next: () => {
        this.successToast('Éxito', 'Documento rechazado');
        this.loadDocuments();
      },
      error: (error) => {
        this.errorToast('Error', error.error?.message || 'Error al rechazar el documento');
      }
    });
  }

  downloadDocument(document: StudentDocument): void {
    this.documentService.downloadDocument(document.id).subscribe({
      next: (blob) => {
        const media = document.media?.[0];
        const filename = media?.file_name || 'documento';
        const url = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = filename;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error al descargar documento:', error);
        this.errorToast('Error', 'Error al descargar el documento');
      }
    });
  }

  getDocumentUrl(document: StudentDocument): string {
    return document.media?.[0]?.original_url || '';
  }

  getExpirationStatusClass(document: StudentDocument): string {
    if (!document.expiration_date) return '';
    if (document.is_expired) return 'text-red-600 bg-red-100';
    if (document.days_until_expiration !== null && document.days_until_expiration <= 30) {
      return 'text-yellow-600 bg-yellow-100';
    }
    return 'text-green-600 bg-green-100';
  }

  formatDate(date: string | null): string {
    if (!date) return '-';
    // Extraer solo la parte de la fecha (YYYY-MM-DD) para evitar problemas de zona horaria
    const datePart = date.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  }

  handleConfirm(): void {
    if (this.confirmationAction) {
      this.confirmationAction();
    }
    this.showConfirmation = false;
    this.confirmationAction = null;
  }

  handleCancel(): void {
    this.showConfirmation = false;
    this.confirmationAction = null;
  }

  successToast(title: string, message: string): void {
    const opt: any = this.options;
    opt.toastComponent = AlertToast;
    opt.progressBar = true;
    opt.type = 'success';
    opt.toastClass = 'toast';
    this.toastr.show(message, title, opt);
  }

  errorToast(title: string, msg: string): void {
    const opt: any = this.options;
    opt.toastComponent = AlertToast;
    opt.progressBar = true;
    opt.type = 'error';
    opt.toastClass = 'toast';
    this.toastr.show(msg, title, opt);
  }
}
